import { MeetingToken } from '@/types/booking';
import { User } from '@/types/user';
import { useAuth } from '@clerk/nextjs';
import axios, { AxiosResponse } from 'axios';

export const useUserService = () => {
  const { getToken } = useAuth();

  const getMeetingToken = async (
    bookingId: string,
    userId: string,
    isExpert: boolean,
  ): Promise<MeetingToken> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<MeetingToken> = await axios.get<MeetingToken>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/${userId}/bookings/${bookingId}/meeting/token?isExpert=${isExpert}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user meeting token:', error);
      throw new Error('Failed to fetch meeting token');
    }
  };

  const getCourseToken = async (bookingId: string): Promise<MeetingToken> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<MeetingToken> = await axios.get<MeetingToken>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/bookings/${bookingId}/course/token`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user meeting token:', error);
      throw new Error('Failed to fetch meeting token');
    }
  };

  const searchUsers = async (query: string): Promise<User[]> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<User[]> = await axios.get<User[]>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/search`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { query },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error searching for users:', error);
      throw new Error('Failed to search for users');
    }
  };

  const getUserById = async (userId: string): Promise<User> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<User> = await axios.get<User>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching user by ID: ${userId}`, error);
      throw new Error('Failed to fetch user');
    }
  };

  const getUserByAuthUserId = async (authUserId: string): Promise<User | undefined> => {
    const token = await getToken();
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/authUserId/${authUserId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error(`Error: ${res.statusText}`);
      }

      return await res.json();
    } catch (e) {
      console.error('Error fetching user by ID', e);
    }
  };

  const getNumberOfRemainingSessionsOfUser = async (userId: string): Promise<number> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<number> = await axios.get<number>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/numberOfSessionRemaining/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user meeting token:', error);
      throw new Error('Failed to fetch meeting token');
    }
  };

  const updateLanguage = async (id: string | undefined, language: string): Promise<boolean> => {
    if (typeof id == 'undefined') {
      throw new Error('User id not set for language update.');
    }
    const token = await getToken();
    try {
      await axios.patch<number>(
        `${process.env.NEXT_PUBLIC_BACKEND_API_URL}/users/${id}/language`,
        {
          language,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return true;
    } catch (error) {
      console.error('Error updating user language:', error);
      throw new Error('Error updating user language');
    }
  };

  return {
    getMeetingToken,
    getCourseToken,
    searchUsers,
    getUserById,
    getUserByAuthUserId,
    getNumberOfRemainingSessionsOfUser,
    updateLanguage,
  };
};
